/** Shared document checklist (Demo Mode only) - reuses the grouped-accordion, upload/approve/
 * download/delete structure established by the OFD retailer onboarding wizard's due diligence
 * tab (`pages/orderly-for-distributors/Onboarding/components/OnboardingDueDiligence.tsx`), so
 * Customer Management doesn't invent a new document pattern. Local/mocked only - there is no
 * Customer Management document backend yet, so upload/approve/delete mutate the caller's draft
 * via `onChange` and download generates a placeholder file client-side.
 *
 * `mode="view"` (e.g. the operational Contracts tab) only shows the download action.
 * `mode="manage"` (e.g. the onboarding wizard) also shows upload/approve/delete, matching the
 * OFD due diligence tab's capture flow. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, Grid, IconButton, Stack,
  Tooltip, Typography, useTheme,
} from '@mui/material';
import { Download, UploadFile, DeleteOutline, ExpandMore } from '@mui/icons-material';
import { Pill } from '../../../components/shared/Pill';
import { CustomerDocument } from '../../../mocks/data/customerManagementData';

interface DocumentChecklistProps {
  documents: CustomerDocument[];
  onChange?: (documents: CustomerDocument[]) => void;
  mode?: 'view' | 'manage';
  /** Only render documents under this header, e.g. "Contracts & Legal Agreements". */
  headerFilter?: string;
}

const DocumentChecklist = ({ documents, onChange, mode = 'manage', headerFilter }: DocumentChecklistProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const [openHeader, setOpenHeader] = useState<string | null>(null);

  const visibleDocuments = useMemo(
    () => (headerFilter ? documents.filter((doc) => doc.header === headerFilter) : documents),
    [documents, headerFilter],
  );

  const grouped = useMemo(() => {
    const groups: Record<string, CustomerDocument[]> = {};
    visibleDocuments.forEach((doc) => {
      if (!groups[doc.header]) groups[doc.header] = [];
      groups[doc.header].push(doc);
    });
    return groups;
  }, [visibleDocuments]);

  const updateDocument = (documentId: number, patch: Partial<CustomerDocument>) => {
    if (!onChange) return;
    onChange(documents.map((doc) => (doc.documentId === documentId ? { ...doc, ...patch } : doc)));
  };

  const handleDownload = (doc: CustomerDocument) => {
    const blob = new Blob([`Placeholder content for ${doc.fileName ?? doc.category}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.fileName ?? `${doc.category}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = (doc: CustomerDocument, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      updateDocument(doc.documentId, { fileExists: true, fileName: file.name });
    }
  };

  const handleDelete = (doc: CustomerDocument) => {
    updateDocument(doc.documentId, { fileExists: false, fileName: undefined, approved: false });
  };

  const handleApprove = (doc: CustomerDocument) => {
    updateDocument(doc.documentId, { approved: !doc.approved });
  };

  return (
    <Box>
      {Object.keys(grouped).map((header, index) => {
        const docs = grouped[header];
        const completeCount = docs.filter((doc) => doc.fileExists).length;
        return (
          <Accordion
            key={header}
            defaultExpanded={index === 0}
            expanded={openHeader === null ? index === 0 : openHeader === header}
            onChange={(_, expanded) => setOpenHeader(expanded ? header : null)}
            variant="borderless"
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row"
                spacing={2}
                alignItems="center">
                <Typography variant="textSM"
                  fontWeight={600}>{header}</Typography>
                <Pill text={`${completeCount}/${docs.length} ${t('documentChecklistUploaded', 'uploaded')}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container
                spacing={2}>
                {docs.map((doc) => (
                  <Grid item
                    xs={12}
                    sm={6}
                    key={doc.documentId}>
                    <Stack direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ p: 1 }}>
                      {mode === 'manage' && (
                        <Tooltip title={t('documentChecklistApprove', 'Approve')}
                          arrow>
                          <span>
                            <Checkbox
                              size="small"
                              checked={doc.approved}
                              disabled={!doc.fileExists}
                              onChange={() => handleApprove(doc)}
                            />
                          </span>
                        </Tooltip>
                      )}
                      <Typography variant="textSM"
                        sx={{ flex: 1, color: doc.fileExists ? theme.palette.custom.gray[900] : theme.palette.custom.gray[600] }}>
                        {doc.category}
                      </Typography>
                      {mode === 'manage' && (
                        <>
                          <input aria-hidden
                            type="file"
                            id={`customer-doc-upload-${doc.documentId}`}
                            hidden
                            onChange={(event) => handleUpload(doc, event)}
                          />
                          <IconButton
                            size="small"
                            title={t('documentChecklistUpload', 'Upload')}
                            disabled={doc.approved}
                            onClick={() => document.getElementById(`customer-doc-upload-${doc.documentId}`)?.click()}
                          >
                            <UploadFile fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        size="small"
                        title={t('documentChecklistDownload', 'Download')}
                        disabled={!doc.fileExists}
                        onClick={() => handleDownload(doc)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                      {mode === 'manage' && (
                        <IconButton
                          size="small"
                          title={t('documentChecklistDelete', 'Delete')}
                          disabled={!doc.fileExists || doc.approved}
                          onClick={() => handleDelete(doc)}
                        >
                          <DeleteOutline fontSize="small"
                            color="error" />
                        </IconButton>
                      )}
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default DocumentChecklist;
